/* eslint-disable react/no-danger */
import React from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';

import { Button, Input } from 'components/ui';
import { cx } from 'helpers/StylingHelpers';
import { useToast } from 'hooks/common/useToast';
import { useTranslation } from 'hooks/common/useTranslation';
import { useAccountMutation } from 'hooks/data/useAccountMutation';
import { useConfig } from 'hooks/data/useConfig';
import { useUser } from 'hooks/data/useUser';
import { useAnalytics } from 'hooks/useAnalytics';

import styles from './FooterNewsletterSubscribeForm.module.scss';

interface Props {
  className?: string
}

const NewsletterSubscribeForm = (props: Props) => {
  const { className } = props;

  // Hooks

  const { t } = useTranslation();
  const analytics = useAnalytics();

  const toast = useToast();

  const { data: config } = useConfig();
  const { data: user, isError, isLoading } = useUser({ isOptional: true });

  const {
    isLoading: isSubscribeLoading,
    subscribeToNewsletter,
  } = useAccountMutation();

  const validationSchema = React.useMemo(() => yup.object().shape({
    email: yup.string().email(t('VALIDATION_ERRORS.INVALID_EMAIL')).required(t('VALIDATION_ERRORS.REQUIRED'))
  }), [t]);

  // Empty

  if (isLoading || isError || !user) return null;

  // Props

  const { email, subscribedToNewsletter } = user || {};
  const { newsletterFooter } = config || {};
  const { disclaimer, title, header } = newsletterFooter || {};

  // Handlers

  const onSubmit = () => {
    if (subscribedToNewsletter) {
      toast.info(t('FOOTER.NEWSLETTER.ALREADY_SUBSCRIBED'), 'newsletterSubscribeInfo');
      return;
    }

    subscribeToNewsletter({
      onSuccess: () => {
        analytics.subscribeToNewsletterFromFooter();
        toast.success(t('FOOTER.NEWSLETTER.SUCCESS'), 'newsletterSubscribeSuccess');
      }
    });
  };

  // Render

  return (
    <div
      className={cx(
        styles.root,
        className,
        { [styles.subscribed]: !!subscribedToNewsletter }
      )}
    >
      <div className={styles.header}>
        {header}
      </div>
      <div className={styles.title} dangerouslySetInnerHTML={{ __html: title || '' }} />
      <div className={styles.disclaimer}>
        {disclaimer}
      </div>
      <Formik
        enableReinitialize
        initialValues={{ email }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ setFieldError }) => (
          <Form className={styles.form}>
            <Input
              disabled
              className={styles.inputWrapper}
              inputClassName={styles.input}
              errorClassName={styles.error}
              type="email"
              placeholder={t('FOOTER.NEWSLETTER.PLACEHOLDER')}
              name="email"
              onBlur={() => setFieldError('email', undefined)}
            />
            <Button
              type="submit"
              disabled={subscribedToNewsletter}
              className={styles.submitButton}
              variant="primary"
              loading={isSubscribeLoading}
            >
              {t(subscribedToNewsletter ? 'FOOTER.NEWSLETTER.ALREADY_SUBSCRIBED_SHORT' : 'FOOTER.NEWSLETTER.CTA')}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default NewsletterSubscribeForm;
